import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card as AntCard, Col, Empty, Image, Row, Space, Spin, Tag, Tooltip } from 'antd';
import { DeleteOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import { Card } from '@app/components/common/Card/Card';
import { Button } from '@app/components/common/buttons/Button/Button';
import { Popconfirm } from '@app/components/common/Popconfirm/Popconfirm';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { deleteExample, getExamples } from '@app/api/aiDrawExamples.api';
import { SketchMeExampleLanguage, SketchMeExampleModel, SketchMeExampleStatus } from '@app/domain/SketchMeExampleModel';
import { CreateExampleFormModal } from '@app/components/sketchMeExamples/CreateExampleForm/CreateExampleForm';

const POLL_INTERVAL_MS = 15000;

const statusMeta: Record<SketchMeExampleStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'gold' },
  processing: { label: 'Processing', color: 'blue' },
  done: { label: 'Done', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
};

const collagePlaceholderStyle: React.CSSProperties = {
  height: 180,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
};

const I18N_LANGUAGES: SketchMeExampleLanguage[] = ['en', 'fr', 'de', 'es', 'it'];

const ExampleName: React.FC<{ example: SketchMeExampleModel }> = ({ example }) => {
  const nameI18n = example.name_i18n;
  if (!nameI18n) {
    return <>{example.name}</>;
  }
  return (
    <Space size={6}>
      {example.name}
      <Tooltip
        title={
          <div>
            {I18N_LANGUAGES.filter((lang) => nameI18n[lang]).map((lang) => (
              <div key={lang}>
                {lang.toUpperCase()}: {nameI18n[lang]}
              </div>
            ))}
          </div>
        }
      >
        <GlobalOutlined style={{ color: 'var(--primary-color)', cursor: 'pointer' }} />
      </Tooltip>
    </Space>
  );
};

const ExampleCollage: React.FC<{ example: SketchMeExampleModel }> = ({ example }) => {
  if (example.status === 'done' && example.steps && example.steps.length > 0) {
    const steps = [...example.steps].sort((a, b) => a.step_no - b.step_no);
    return (
      <Image.PreviewGroup>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 4,
            padding: 8,
          }}
        >
          {steps.map((step) => (
            <Image
              key={step.step_no}
              src={step.image_url}
              alt={step.label}
              style={{ width: '100%', height: 72, objectFit: 'cover' }}
            />
          ))}
        </div>
      </Image.PreviewGroup>
    );
  }

  if (example.status === 'failed') {
    return (
      <div style={collagePlaceholderStyle}>
        <Tag color="red">Generation failed</Tag>
      </div>
    );
  }

  return (
    <div style={collagePlaceholderStyle}>
      <Spin />
      <Tag color={statusMeta[example.status]?.color ?? 'default'}>
        {example.status === 'processing' ? 'Processing…' : 'Pending…'}
      </Tag>
    </div>
  );
};

const SketchMeExamplesPage: React.FC = () => {
  const [examples, setExamples] = useState<SketchMeExampleModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { isMounted } = useMounted();

  const fetchList = useCallback(
    (silent = false) => {
      if (!silent) {
        setLoading(true);
      }
      getExamples()
        .then((res) => {
          if (isMounted.current) {
            setExamples(res.data ?? []);
          }
        })
        .catch(() => {
          // Error toast is already shown by the httpApi response interceptor.
        })
        .finally(() => {
          if (!silent && isMounted.current) {
            setLoading(false);
          }
        });
    },
    [isMounted],
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Poll while any example is still generating; stop when none are in flight.
  const hasInFlight = useMemo(
    () => examples.some((example) => example.status === 'pending' || example.status === 'processing'),
    [examples],
  );

  useEffect(() => {
    if (!hasInFlight) {
      return undefined;
    }
    const timer = setInterval(() => fetchList(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasInFlight, fetchList]);

  const handleDelete = (id: number | string) => {
    deleteExample(id)
      .then(() => {
        notificationController.success({
          message: 'Example deleted',
          description: 'Users will no longer see it.',
        });
        fetchList(true);
      })
      .catch(() => {
        // Error toast is already shown by the httpApi response interceptor.
      });
  };

  return (
    <>
      <Card
        padding="1.25rem 1.875rem"
        title="Sketch-Me Examples"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateOpen(true)}>
            Create Example
          </Button>
        }
      >
        <Spin spinning={loading}>
          {examples.length === 0 && !loading ? (
            <Empty description="No examples yet. Create one to get started." />
          ) : (
            <Row gutter={[16, 16]}>
              {examples.map((example) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={example.id}>
                  <AntCard
                    size="small"
                    cover={<ExampleCollage example={example} />}
                    actions={[
                      <Popconfirm
                        key="delete"
                        title="Delete this example? Users will no longer see it."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(example.id)}
                      >
                        <Button type="link" danger icon={<DeleteOutlined />} size="small">
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <AntCard.Meta
                      title={<ExampleName example={example} />}
                      description={
                        <Space size={[4, 4]} wrap>
                          <Tag color={statusMeta[example.status]?.color ?? 'default'}>
                            {statusMeta[example.status]?.label ?? example.status}
                          </Tag>
                        </Space>
                      }
                    />
                  </AntCard>
                </Col>
              ))}
            </Row>
          )}
        </Spin>
      </Card>
      <CreateExampleFormModal
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        onFinish={() => {
          setIsCreateOpen(false);
          fetchList(true);
        }}
      />
    </>
  );
};

export default SketchMeExamplesPage;
